import { useEffect, useState, useContext, useMemo } from "react";
import { AuthContext } from "../../../Contexts/AuthContext";
import { getTodayTasksA, markDoneA, markUnDoneA } from "../../../api/todoApi";
import { FiCheckSquare, FiCalendar, FiFileText, FiCheck, FiRotateCcw, FiLoader, FiUser } from "react-icons/fi";
import { GiCow } from "react-icons/gi";
import "../../eleveur/ToDoToday/toDoToday.css";
import { useTranslation } from "react-i18next";
import { getTodayTasks, markDone, markUnDone } from "../../../api/todoApi";

export default function ToDoTodayAdmin() {
  const { user } = useContext(AuthContext);
  const [tasks, setTasks] = useState([]);
  const { t } = useTranslation();
  const [loading, setLoading] = useState(true);
  const isAdmin = user?.role === 'admin';

  useEffect(() => {
    setLoading(true);
    const fetchTasks = isAdmin ? getTodayTasksA() : getTodayTasks(user.id);
    fetchTasks.then(setTasks).finally(() => setLoading(false));
  }, [isAdmin, user.id]);

  const doneCount = useMemo(() => tasks.filter(tk => tk.is_done).length, [tasks]);
  const pct = tasks.length > 0 ? Math.round((doneCount / tasks.length) * 100) : 0;

  const handleDone = async (id) => {
    if (isAdmin) {
      await markDoneA(id);
    } else {
      await markDone(id);
    }
    setTasks(tasks => tasks.map(tk => tk.id === id ? { ...tk, is_done: true } : tk));
  };

  const handleUnDone = async (id) => {
    if (isAdmin) {
      await markUnDoneA(id);
    } else {
      await markUnDone(id);
    }
    setTasks(tasks => tasks.map(tk => tk.id === id ? { ...tk, is_done: false } : tk));
  };

  // Helper to get translated title/desc
  const getTranslatedTitle = (task) => {
    if (task.type) {
      return t(`todoTodayAdmin.types.${task.type}.title`, { animal: task.animal_name });
    }
    return task.title;
  };
  const getTranslatedDesc = (task) => {
    if (task.type) {
      return t(`todoTodayAdmin.types.${task.type}.desc`, { animal: task.animal_name });
    }
    return task.description;
  };

  return (
    <div className="td-page">
      {/* Header */}
      <div className="td-header">
        <div className="td-header-icon"><FiCheckSquare /></div>
        <div>
          <h1 className="td-title">{t("todoTodayAdmin.title")}</h1>
          <p className="td-subtitle">{t("todoTodayAdmin.subtitle") || "Today's veterinary tasks and follow-ups"}</p>
        </div>
      </div>

      {/* Progress */}
      {!loading && tasks.length > 0 && (
        <div className="td-progress">
          <div className="td-progress-info">
            <span className="td-progress-label">{doneCount}/{tasks.length} {t("todoTodayAdmin.completed") || "completed"}</span>
            <span className="td-progress-pct">{pct}%</span>
          </div>
          <div className="td-progress-bar">
            <div className="td-progress-fill" style={{ width: `${pct}%` }} />
          </div>
        </div>
      )}

      {/* Loading */}
      {loading && (
        <div className="td-loading">
          <FiLoader className="td-loading-icon" />
          <span>{t("todoTodayAdmin.loading")}</span>
        </div>
      )}

      {/* Empty */}
      {!loading && tasks.length === 0 && (
        <div className="td-empty">
          <FiCheckSquare className="td-empty-icon" />
          <h3>{t("todoTodayAdmin.emptyTitle") || t("todoTodayAdmin.empty")}</h3>
          <p>{t("todoTodayAdmin.emptySubtitle") || "All caught up for today!"}</p>
        </div>
      )}

      {/* Task List */}
      {!loading && tasks.length > 0 && (
        <div className="td-list">
          {tasks.map(task => (
            <div key={task.id} className={`td-card ${task.is_done ? 'td-done' : ''}`}>
              <div className="td-card-header">
                <span className="td-date"><FiCalendar /> {task.todo_date}</span>
                <span className={`td-status ${task.is_done ? 'td-status-done' : 'td-status-pending'}`}> 
                  {task.is_done ? (t("todoTodayAdmin.statusDone") || "Done") : (t("todoTodayAdmin.statusPending") || "Pending")}
                </span>
              </div>

              <div className="td-card-body">
                <h3 className="td-task-title"><FiFileText /> {getTranslatedTitle(task)}</h3>
                <p className="td-desc">{getTranslatedDesc(task)}</p>
                <div className="td-meta">
                  {isAdmin && (
                    <span className="td-responsible"><FiUser /> {t("todoTodayAdmin.responsible")} : {task.reproduction?.animal?.user?.nom}</span>
                  )}
                  <span className="td-animal"><GiCow /> {t("todoTodayAdmin.animal")} : {task.reproduction?.animal?.numero} - {task.reproduction?.animal?.nom}</span>
                </div>
              </div>

              <div className="td-card-footer">
                {!task.is_done ? (
                  <button onClick={() => handleDone(task.id)} className="td-btn td-btn-done">
                    <FiCheck /> {t("todoTodayAdmin.buttons.done")}
                  </button>
                ) : (
                  <button onClick={() => handleUnDone(task.id)} className="td-btn td-btn-undo">
                    <FiRotateCcw /> {t("todoTodayAdmin.buttons.undo")}
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}